import {
  Card,
  CardContent,
  FormControlLabel,
  FormHelperText,
  Grid,
  List,
  ListItem,
  makeStyles,
  Paper,
  Switch,
  TextField
} from "@material-ui/core";
import { InfoOutlined } from "@material-ui/icons";
import CardTitle from "@saleor/components/CardTitle";
import ControlledCheckbox from "@saleor/components/ControlledCheckbox";
import Hr from "@saleor/components/Hr";
import { RadioGroupField } from "@saleor/components/RadioGroupField";
import SingleSelectField from "@saleor/components/SingleSelectField";
import { User_userPermissions } from "@saleor/fragments/types/User";
import { FormChange } from "@saleor/hooks/useForm";
import useUser from "@saleor/hooks/useUser";
import { maybe } from "@saleor/misc";
import { CouponUserTypeEnum } from "@saleor/types/globalTypes";
import React, { useState } from "react";
import { useIntl } from "react-intl";

import { canManageCouponUsageLimitByDealType } from "../../utils";
import { CouponCreateFormData } from "../CouponCreatePage";
import { TooltipArrowUp } from "../CouponDetails/Tooltip";
import { convertTimeFormat, couponTimerOptions, useTimerUnits } from "./utils";

export interface CouponUsageProps {
  disabled: boolean;
  data: CouponCreateFormData;
  onChange: FormChange;
  localError: {
    usageLimit: string;
    usageTimerHour: string;
    usageTimerMinute: string;
    usageTimerSecond: string;
  };
  userPermissions?: User_userPermissions[];
}

const useStyles = makeStyles(
  () => ({
    card: {
      overflow: "visible"
    },
    cardTitle: {
      "& span": {
        fontWeight: "bold"
      }
    },
    errorMessage: {
      fontSize: "15px",
      marginTop: "4px",
      padding: "4px"
    },
    isHidden: {
      "& span": {
        fontSize: "15px"
      },
      marginBottom: "10px",
      marginTop: "10px"
    },
    isRelative: {
      position: "relative"
    },
    isUsageTimerApplied: {
      "& span": {
        fontSize: "17px"
      },
      marginTop: "5px"
    },
    isUsageTimerAppliedContainer: {
      alignItems: "center",
      display: "flex"
    },
    radioField: {
      marginTop: 0
    },
    test: {
      "&::-webkit-datetime-edit-ampm-field": {
        display: "none"
      }
    },
    tooltipIcon: {
      marginLeft: "4px"
    },
    tooltipLabel: {
      alignItems: "center",
      display: "flex"
    },
    usageInput: {
      padding: "4px !important"
    },
    usageInputWrapper: {
      padding: "10px"
    },
    usageLimitTextfield: {
      paddingBottom: "15px"
    },
    usageSelections: {
      left: "0",
      maxHeight: "30dvh",
      overflowY: "auto",
      position: "absolute",
      top: "4em",
      width: "100%",
      zIndex: 10
    },
    usageTxt: {
      color: "#747C8C",
      fontSize: "1rem",
      fontWeight: "bold"
    }
  }),
  { name: "CouponUsage" }
);

export const CouponUsage: React.FC<CouponUsageProps> = props => {
  const {
    data,
    disabled,
    onChange,
    localError,
    userPermissions: propPermissions
  } = props;
  const intl = useIntl();
  const classes = useStyles({});
  const { user } = useUser();
  const userPermissions =
    propPermissions !== undefined
      ? propPermissions
      : user?.userPermissions || [];
  const [
    userRestrictionTooltipHover,
    setUserRestrictionTooltipHover
  ] = useState(false);
  const [userRestrictionSelectOpen, setUserRestrictionSelectOpen] = useState(
    false
  );

  const canManageCouponUsageLimit = canManageCouponUsageLimitByDealType(
    userPermissions,
    data?.couponClassification
  );
  const userRestrictionChoices = [
    {
      label: "All Users",
      value: CouponUserTypeEnum.ALL
    },
    {
      label: "New User",
      value: CouponUserTypeEnum.NEW
    },
    {
      label: "Existing User",
      value: CouponUserTypeEnum.EXISTING
    }
  ];

  const usageLimitChoices = [
    {
      label: "Once",
      value: "once"
    },
    {
      label: (
        <div className={classes.tooltipLabel}>
          <span>Multiple</span>
          <TooltipArrowUp
            title="Choose if one user can redeem multiple times​."
            placement="bottom"
          >
            <InfoOutlined color="primary" className={classes.tooltipIcon} />
          </TooltipArrowUp>
        </div>
      ),
      value: "multiple"
    }
  ];

  const handleUsageLimit = event => {
    const e = event.target.value;
    if (e === "once") {
      onChange({
        target: {
          name: "usageLimitToggle",
          value: "once"
        }
      });

      onChange({
        target: {
          name: "usageLimit",
          value: 1
        }
      });
    } else if (e === "multiple") {
      onChange({
        target: {
          name: "usageLimitToggle",
          value: "multiple"
        }
      });

      onChange({
        target: {
          name: "usageLimit",
          value: data.usageLimit || 1
        }
      });
    } else {
      const value = e.replace(/[^0-9]/g, "");
      onChange({
        target: {
          name: "usageLimit",
          value: value === "" ? "" : Number(value)
        }
      });
    }
  };

  const handleCustomTimerToggle = event => {
    const isChecked = event.target.checked;

    if (!isChecked) {
      localError.usageTimerHour = null;
      localError.usageTimerMinute = null;
      localError.usageTimerSecond = null;
    }

    onChange({
      target: {
        name: event.target.name,
        value: isChecked
      }
    });

    onChange({
      target: {
        name: "usageTimerHour",
        value: !isChecked ? 23 : ""
      }
    });

    onChange({
      target: {
        name: "usageTimerMinute",
        value: !isChecked ? 59 : ""
      }
    });

    onChange({
      target: {
        name: "usageTimerSecond",
        value: !isChecked ? 59 : ""
      }
    });
  };

  const handleTimerChange = event => {
    const [hours, minutes, seconds] = event.target.value.split(":");

    onChange({
      target: {
        name: "usageTimerHour",
        value: parseInt(hours, 10)
      }
    });

    onChange({
      target: {
        name: "usageTimerMinute",
        value: parseInt(minutes, 10)
      }
    });

    onChange({
      target: {
        name: "usageTimerSecond",
        value: parseInt(seconds, 10)
      }
    });
  };

  const toggleApplyUsageTimer = event => {
    data.usageTimerHour = 23;
    data.usageTimerMinute = 59;
    data.usageTimerSecond = 59;
    if (event.target.value === false) {
      data.isUsageCustomTimer = false;
      data.usageTimerHour = 23;
      data.usageTimerMinute = 59;
      data.usageTimerSecond = 59;
      localError.usageTimerHour = null;
      localError.usageTimerMinute = null;
      localError.usageTimerSecond = null;
    }
    return onChange(event);
  };

  return (
    <Card>
      <CardTitle
        className={classes.cardTitle}
        title={intl.formatMessage({
          defaultMessage: "Coupon Usage",
          description: "section header"
        })}
      />
      <CardContent>
        <TooltipArrowUp
          title="Select who is allowed to redeem the coupon."
          placement="bottom"
          open={userRestrictionTooltipHover && !userRestrictionSelectOpen}
          disableFocusListener
          disableTouchListener
          onOpen={() => setUserRestrictionTooltipHover(true)}
          onClose={() => setUserRestrictionTooltipHover(false)}
        >
          <div>
            <SingleSelectField
              name="userRestriction"
              choices={userRestrictionChoices}
              onChange={onChange}
              label={intl.formatMessage({
                defaultMessage: "User Restriction"
              })}
              value={data.userRestriction}
              disabled={disabled}
              selectProps={{
                onClose: () => setUserRestrictionSelectOpen(false),
                onOpen: () => setUserRestrictionSelectOpen(true)
              }}
            />
          </div>
        </TooltipArrowUp>
        <TooltipArrowUp
          title="Check this if the coupon is private or for a selected group of people only."
          placement="bottom"
        >
          <div>
            <ControlledCheckbox
              id="isHiddenCheckbox"
              name="isHidden"
              checked={data.isHidden}
              label={intl.formatMessage({
                defaultMessage: "The coupon will only be accessed via link"
              })}
              className={classes.isHidden}
              disabled={disabled}
              onChange={onChange}
            />
          </div>
        </TooltipArrowUp>
        {canManageCouponUsageLimit && (
          <>
            <div className={classes.usageTxt}>
              {intl.formatMessage({
                defaultMessage: "USAGE LIMIT"
              })}
            </div>
            <RadioGroupField
              name="usageLimit"
              className={classes.radioField}
              choices={usageLimitChoices}
              disabled={disabled}
              value={data.usageLimitToggle}
              onChange={e => handleUsageLimit(e)}
            />
            {data.usageLimitToggle === "multiple" && (
              <TextField
                id="usageLimitTxt"
                className={classes.usageLimitTextfield}
                name="usageLimit"
                label={intl.formatMessage({
                  defaultMessage: "Coupon Usage Limit No."
                })}
                type="number"
                onChange={e => handleUsageLimit(e)}
                fullWidth
                onKeyDown={event => {
                  const allowedKeys = ["Backspace", "ArrowLeft", "ArrowRight"];

                  if (
                    event.metaKey ||
                    event.ctrlKey ||
                    allowedKeys.includes(event.key)
                  ) {
                    return;
                  }

                  if (!/[0-9]/.test(event.key)) {
                    event.preventDefault();
                  }
                }}
                disabled={disabled}
                value={data.usageLimit ?? ""}
                error={!!localError.usageLimit}
                helperText={maybe(() => localError.usageLimit, "")}
              />
            )}
          </>
        )}
        <Hr />
        <div className={classes.isUsageTimerAppliedContainer}>
          <ControlledCheckbox
            id="isUsageTimerAppliedCheckbox"
            name="isUsageTimerApplied"
            checked={data.isUsageTimerApplied}
            label={intl.formatMessage({
              defaultMessage: "Apply Usage Timer"
            })}
            className={classes.isUsageTimerApplied}
            disabled={disabled}
            onChange={e => toggleApplyUsageTimer(e)}
          />
          <TooltipArrowUp
            title="Check if the coupon requires time limit for redemption."
            placement="bottom"
          >
            <InfoOutlined color="primary" />
          </TooltipArrowUp>
        </div>
        {data.isUsageTimerApplied && !data.isUsageCustomTimer ? (
          <CouponUsageTimerSelection
            handleTimerChange={handleTimerChange}
            data={data}
            disabled={disabled}
          />
        ) : null}
        {data.isUsageTimerApplied ? (
          <FormControlLabel
            control={
              <Switch
                id="isUsageCustomTimerToggle"
                name="isUsageCustomTimer"
                checked={data.isUsageCustomTimer}
                onChange={disabled ? undefined : handleCustomTimerToggle}
                disabled={disabled}
                color="primary"
              />
            }
            label="Set custom timer"
          />
        ) : null}
        {data.isUsageCustomTimer && data.isUsageTimerApplied ? (
          <CouponUsageTimer
            data={data}
            onChange={onChange}
            disabled={disabled}
            localError={localError}
          />
        ) : null}
      </CardContent>
    </Card>
  );
};

const CouponUsageTimerSelection = ({ handleTimerChange, data, disabled }) => {
  const hours = convertTimeFormat(data.usageTimerHour);
  const minutes = convertTimeFormat(data.usageTimerMinute);
  const seconds = convertTimeFormat(data.usageTimerSecond);

  return (
    <SingleSelectField
      name="setTimer"
      choices={couponTimerOptions}
      onChange={handleTimerChange}
      value={
        hours === null && minutes === null && seconds === null
          ? `23:59:59`
          : `${hours}:${minutes}:${seconds}`
      }
      disabled={disabled}
      label="Set Timer"
    />
  );
};

const CouponUsageTimer = ({ disabled, data, onChange, localError }) => {
  const { hours, minutes, seconds } = useTimerUnits();
  const classes = useStyles({});
  const time = {
    hours: data.usageTimerHour,
    minutes: data.usageTimerMinute,
    seconds: data.usageTimerSecond
  };

  return (
    <Grid container spacing={3} className={classes.usageInputWrapper}>
      <Grid item xs={4} className={classes.usageInput}>
        <CouponUsageTimePicker
          label="hours"
          name="usageTimerHour"
          options={hours}
          value={time.hours}
          onChange={onChange}
          disabled={disabled}
          key={"hours"}
          localError={localError}
        />
      </Grid>
      <Grid item xs={4} className={classes.usageInput}>
        <CouponUsageTimePicker
          label="minutes"
          name="usageTimerMinute"
          options={minutes}
          value={time.minutes}
          onChange={onChange}
          disabled={disabled}
          key={"minutes"}
          localError={localError}
        />
      </Grid>
      <Grid item xs={4} className={classes.usageInput}>
        <CouponUsageTimePicker
          label="seconds"
          name="usageTimerSecond"
          options={seconds}
          value={time.seconds}
          onChange={onChange}
          disabled={disabled}
          key={"seconds"}
          localError={localError}
        />
      </Grid>
      <FormHelperText className={classes.errorMessage} error={true}>
        {localError.usageTimerHour ||
          localError.usageTimerMinute ||
          localError.usageTimerSecond}
      </FormHelperText>
    </Grid>
  );
};

const CouponUsageTimePicker = ({
  label,
  name,
  options,
  value,
  onChange,
  disabled,
  localError
}) => {
  const classes = useStyles({});
  const [open, setOpen] = useState(false);
  const handleChange = event => {
    const { value } = event.target;
    const isValidTime = value.length
      ? !!options.find(option => {
          if (value.length === 1) {
            return option.startsWith(value);
          }

          return option.toString() === value.toString();
        })
      : true;

    if (isValidTime || value === "") {
      onChange(event);
    }
  };

  const filteredOptions = value?.length
    ? options.filter(option => {
        if (value.length === 1) {
          return option.startsWith(value);
        }

        return option.toString() === value.toString();
      })
    : options;

  return (
    <>
      <div className={classes.isRelative}>
        <TextField
          id={`${name}Txt`}
          name={name}
          value={value}
          disabled={disabled}
          onChange={handleChange}
          onKeyPress={event => {
            if (!/[0-9]/.test(event.key)) {
              event.preventDefault();
            }
          }}
          onFocus={() => setOpen(true)}
          label={label}
          InputProps={{
            autoComplete: "off"
          }}
          InputLabelProps={{
            shrink: true
          }}
          placeholder="--"
          error={!!localError[name]}
        />
        {open ? (
          <Paper className={classes.usageSelections}>
            <List component={"ul"}>
              {filteredOptions.map((option, index) => (
                <ListItem
                  id={`${name}Dropdown${index}`}
                  button
                  onClick={() => {
                    onChange({
                      target: {
                        name,
                        value: option
                      }
                    });
                    setOpen(false);
                  }}
                  key={option}
                >
                  {option}
                </ListItem>
              ))}
            </List>
          </Paper>
        ) : null}
      </div>
    </>
  );
};

export default CouponUsage;
